import { createAuthenticatedFetch } from './types/core/common';
import type { 
  SchemaTranslationCreate, 
  SchemaTranslationResponse, 
  SchemaTranslationUpdate,
  SectionWithTranslationResponse 
} from './types';

// Types pour les réponses étendues avec détails
export interface SchemaTranslationWithDetails {
  id: number;
  language_code: string;
  titre: string;
  description: string;
  created_at: string;
  updated_at: string;
  schema_model: {
    uuid: string;
    appellation: string;
    type: string;
    description: string;
  };
  section: {
    id: number;
    name: string;
    type: string;
  };
}

// @deprecated Use imports from '@/api/types' instead
export type {
  SchemaTranslationCreate,
  SchemaTranslationResponse,
  SchemaTranslationUpdate,
  SectionWithTranslationResponse,
};

const api = createAuthenticatedFetch();

export const schemaTranslations = {
  /**
   * Créer une nouvelle traduction de schéma
   */
  async createTranslation(translation: SchemaTranslationCreate): Promise<SchemaTranslationResponse> {
    return await api.post<SchemaTranslationResponse>('/schema-translations/', translation);
  },

  /**
   * Récupérer toutes les traductions de base
   */
  async fetchAllTranslations(): Promise<SchemaTranslationResponse[]> {
    try {
      return await api.get<SchemaTranslationResponse[]>('/schema-translations/');
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        console.log('⚠️ API schema-translations non disponible, retour tableau vide');
        return [];
      }
      throw error;
    }
  },

  /**
   * Récupérer toutes les traductions avec détails (schéma et section)
   */
  async fetchAllTranslationsWithDetails(): Promise<SchemaTranslationWithDetails[]> {
    try {
      return await api.get<SchemaTranslationWithDetails[]>('/schema-translations/all-schemas-translations');
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        console.log('⚠️ API schema-translations détaillées non disponible, retour tableau vide');
        return [];
      }
      throw error;
    }
  },

  /**
   * Récupérer une traduction spécifique
   */
  async fetchTranslation(translationId: number): Promise<SchemaTranslationResponse> {
    return await api.get<SchemaTranslationResponse>(`/schema-translations/${translationId}`);
  },

  /**
   * Mettre à jour une traduction
   */
  async updateTranslation(translationId: number, update: SchemaTranslationUpdate): Promise<SchemaTranslationResponse> {
    return await api.put<SchemaTranslationResponse>(`/schema-translations/${translationId}`, update);
  },

  /**
   * Supprimer une traduction
   */
  async deleteTranslation(translationId: number): Promise<void> {
    await api.delete(`/schema-translations/${translationId}`);
  },

  /**
   * Récupérer les sections avec leurs traductions pour un schéma et une langue
   */
  async fetchSectionsWithTranslations(
    schemaUuid: string, 
    languageCode: string
  ): Promise<SectionWithTranslationResponse[]> {
    return await api.get<SectionWithTranslationResponse[]>(
      `/schema-translations/schema/${schemaUuid}/sections-with-translations/${languageCode}`
    );
  },

  /**
   * Récupérer toutes les traductions d'un schéma spécifique
   */
  async fetchSchemaTranslations(schemaUuid: string): Promise<SchemaTranslationResponse[]> {
    const allTranslations = await this.fetchAllTranslations();
    return allTranslations.filter((t: SchemaTranslationResponse) => t.schema_uuid === schemaUuid);
  },

  /**
   * Récupérer toutes les traductions d'un schéma spécifique avec détails
   */
  async fetchSchemaTranslationsWithDetails(schemaUuid: string): Promise<SchemaTranslationWithDetails[]> {
    const allTranslations = await this.fetchAllTranslationsWithDetails();
    return allTranslations.filter((t: SchemaTranslationWithDetails) => t.schema_model.uuid === schemaUuid);
  },

  /**
   * Supprimer toutes les traductions d'un schéma
   */
  async deleteSchemaTranslations(schemaUuid: string): Promise<void> {
    const translations = await this.fetchSchemaTranslations(schemaUuid);
    for (const translation of translations) {
      await this.deleteTranslation(translation.id);
    }
  },

  /**
   * Créer plusieurs traductions en lot
   */
  async createBulkTranslations(translations: SchemaTranslationCreate[]): Promise<SchemaTranslationResponse[]> {
    const results: SchemaTranslationResponse[] = [];
    for (const translation of translations) {
      try {
        const result = await this.createTranslation(translation);
        results.push(result);
      } catch (error) {
        console.error('Erreur création traduction:', translation, error);
        throw error;
      }
    }
    return results;
  }
};

// Exporter les fonctions individuellement
export const {
  createTranslation,
  fetchAllTranslations,
  fetchAllTranslationsWithDetails,
  fetchTranslation,
  updateTranslation,
  deleteTranslation,
  fetchSectionsWithTranslations,
  fetchSchemaTranslations,
  fetchSchemaTranslationsWithDetails,
  deleteSchemaTranslations,
  createBulkTranslations
} = schemaTranslations; 